import { useMutation, useQueryClient } from '@tanstack/react-query';
import { msg } from '@lingui/core/macro';
import { LateExtrasService } from '@/api';
import type { lateextras_LateExtrasRegenerateResponse } from '@/api';
import { invoiceKeys } from '@/hooks/useInvoices';
import { workflowKeys } from '@/hooks/useWorkflows';

// Late extras after invoicing (#2028). Regenerate cancels issued invoices and
// creates replacement drafts, so it refreshes the inbox and every invoice list.

export type LateExtrasRegenerateResult = lateextras_LateExtrasRegenerateResponse;

export function useRegenerateLateExtras(itemId: string) {
  const queryClient = useQueryClient();
  return useMutation<LateExtrasRegenerateResult, unknown, void>({
    mutationFn: () => LateExtrasService.postApiV1LateExtrasRegenerate(itemId),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: workflowKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() }),
        queryClient.invalidateQueries({ queryKey: workflowKeys.stats() }),
        queryClient.invalidateQueries({ queryKey: workflowKeys.detail(itemId) }),
        queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
      ]);
    },
    meta: {
      errorFallback: msg({
        id: 'lateExtras.regenerate.error',
        message: 'The invoices could not be re-issued. Please try again.',
      }),
    },
  });
}
